import { useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  DoorOpen,
  Languages,
  TriangleAlert,
  Volume2,
  VolumeX,
  X,
  XCircle,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  type BulkAlertResult,
  useAcknowledgeAlert,
  useAlerts,
  useBulkAcknowledgeAlerts,
  useBulkClearAlerts,
  useClearAlert,
} from "@/shared/api/hooks/alerts";
import { useSites } from "@/shared/api/hooks/sites";
import { qk } from "@/shared/api/keys";
import { useAlertChannel } from "@/shared/api/ws";
import { selectUser, useAuth } from "@/shared/auth/store";
import { useCan } from "@/shared/auth/permissions";
import { useAlarmSettings } from "@/shared/hooks/useAlarmSettings";
import { useAlertTTS } from "@/shared/hooks/useAlertTTS";
import { changeLanguage, type Language } from "@/shared/i18n";
import {
  ALERT_LANGUAGES,
  gasDisplayName,
  LANG_LABEL,
  levelLabel,
} from "@/shared/i18n/alertPhrases";
import { cn } from "@/shared/lib/cn";
import { Button } from "@/shared/ui/Button";
import { useToast } from "@/shared/ui/Toast";
import { DetailPanel, FeedCard } from "@/widgets/alerts";
import type { Alert } from "@/shared/api/models";

/**
 * AlarmCenter — the PWA critical-alarm surface, redesigned as a **master-detail**
 * full-screen overlay (in <AppShell>).
 *
 * A new active alert at/above the configured level opens the overlay (once per
 * alert id — module-level dedupe survives remounts) and fires the side-effects:
 * vibrate, auto voice (shared dedupe with the passive announcer so it never
 * double-speaks), and an OS Notification when the tab is backgrounded.
 *
 * Layout (vs. the old one-card-per-alert design):
 *   * top    — the selected alert's DETAIL (reuses AlertsPage's DetailPanel),
 *   * bottom — the LIST of every pending alert (reuses AlertsPage's FeedCard);
 *              new alerts **accumulate** here rather than replacing the card,
 *              each row carries its own 확인 / 해제 so the operator can retire
 *              them **one at a time** until the surface empties itself, and the
 *              list is never collapsed behind a "모두 보기" — every pending alert
 *              is reachable by scrolling,
 *   * toolbar — bulk 전체확인 / 전체해제, 음성 정지 (voice off; card stays),
 *               대피, and the voice-language quick-switch (also sets UI language).
 *
 * Clicking a row swaps the detail pane to that alert (master-detail), which is
 * the whole point of keeping the list on screen.
 *
 * The pending set is derived from the query (`state==="open" && !cleared_at &&
 * level>=threshold`), so accumulation + shrink happen for free: acknowledging
 * or clearing an alert drops it from this surface (it stays on the 경보 page),
 * and the last one closes the overlay. `shownAlarms`/seed only decides *when to
 * pop*, never what the list contains — no double-pop, no loop.
 *
 * **Geometry.** The surface is a panel **docked directly under the TopBar**, not
 * a card floating in the middle of the screen (사용자 요구: "상단 메뉴 바로 밑에
 * 배치"). It is deliberately *not* `inset-0`: it starts below the TopBar and
 * right of the Sidebar so the global chrome — 음성 토글, 언어 메뉴, 테마, 계정
 * 메뉴 — stays clickable while an alarm is up. Covering them was the single worst
 * usability bug of the previous design (no way to silence voice). On wide screens
 * it also leaves `--dispatch-dock-w` free on the right so the 119 승인 도크 stays
 * visible beside it instead of being buried underneath.
 */

// Overlay-open dedupe — module-level so remounts don't re-pop the same alert.
const shownAlarms = new Set<number>();

function clampLevel(n: number): 0 | 1 | 2 | 3 {
  if (n >= 3) return 3;
  if (n === 2) return 2;
  if (n === 1) return 1;
  return 0;
}

export function AlarmCenter() {
  const qc = useQueryClient();
  const user = useAuth(selectUser);
  const orgId = user?.organization?.id ?? null;
  // ack=사용자(operator) 이상, clear=사이트관리자 이상 (08 §3, D-R6).
  const canAck = useCan("operator");
  const canClear = useCan("safety_manager");
  const { t } = useTranslation();
  const navigate = useNavigate();

  const overlayEnabled = useAlarmSettings((s) => s.overlayEnabled);
  const vibrate = useAlarmSettings((s) => s.vibrate);
  const notifyBrowser = useAlarmSettings((s) => s.notifyBrowser);
  const threshold = useAlarmSettings((s) => s.threshold);

  const tts = useAlertTTS();

  const { data: alertsData } = useAlerts();
  const { data: sitesData } = useSites();
  const ack = useAcknowledgeAlert();
  const clear = useClearAlert();
  const ackBulk = useBulkAcknowledgeAlerts();
  const clearBulk = useBulkClearAlerts();
  const { toast } = useToast();

  const siteName = useMemo(() => {
    const m = new Map<number, string>();
    (sitesData?.results ?? []).forEach((s) => {
      if (s.id != null) m.set(s.id, s.name);
    });
    return (id: number | string | null | undefined) =>
      id == null ? "" : m.get(Number(id)) ?? "";
  }, [sitesData]);

  // `open` = overlay visible; `selectedId` = which alert shows in the detail
  // pane. Both decoupled from the derived active list so a new alert accumulates
  // in the list without clobbering the operator's current selection.
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  // On first load we adopt the standing backlog as already-seen so we only pop
  // for alerts that fire *after* mount (a genuine push), never the reload set.
  const seededRef = useRef(false);

  // Refresh the alert feed the moment a new alert fires server-side.
  useAlertChannel(orgId, (env) => {
    if (env.type === "alert.fired" || env.type === "alert.cleared") {
      qc.invalidateQueries({ queryKey: qk.safety.alerts.all });
    }
  });

  const alerts = alertsData?.results ?? [];

  // Pending = still **unacknowledged**, not cleared, at/above threshold, newest
  // first. Drives the list column and the new-alert detection below. Ack (or
  // clear) removes the row here — that's the "하나씩 확인해서 없앤다" loop; the
  // full history (incl. acknowledged-but-active) lives on the 경보 page.
  const active = useMemo(
    () =>
      alerts
        .filter(
          (a) => a.state === "open" && !a.cleared_at && (a.level ?? 0) >= threshold,
        )
        .sort((a, b) => +new Date(b.raised_at) - +new Date(a.raised_at)),
    [alerts, threshold],
  );

  // Detect + fire side-effects for the newest qualifying, not-yet-shown alert.
  useEffect(() => {
    if (!overlayEnabled || !alertsData) return;

    // First loaded render: mark the existing backlog seen without popping.
    if (!seededRef.current) {
      active.forEach((a) => a.id != null && shownAlarms.add(a.id));
      seededRef.current = true;
      return;
    }

    const next = active.find((a) => a.id != null && !shownAlarms.has(a.id));
    if (!next || next.id == null) return;

    shownAlarms.add(next.id);
    setSelectedId(next.id);
    setOpen(true);

    // Vibrate (buzz harder for 심각).
    if (vibrate && typeof navigator !== "undefined" && "vibrate" in navigator) {
      try {
        navigator.vibrate(
          (next.level ?? 0) >= 3 ? [300, 150, 300, 150, 500] : [200, 120, 200],
        );
      } catch {
        /* ignore — visual alarm still shows */
      }
    }

    // Auto voice — shares dedupe with the passive announcer (no double-speak).
    tts.announceOne(
      { id: next.id, level: next.level ?? 0, site: next.site, gas: next.gas },
      (id) => siteName(id),
    );

    // OS notification when the operator isn't looking at the tab.
    if (
      notifyBrowser &&
      typeof Notification !== "undefined" &&
      Notification.permission === "granted" &&
      typeof document !== "undefined" &&
      document.hidden
    ) {
      try {
        const lvl = clampLevel(next.level ?? 0);
        const gas = gasDisplayName(tts.lang, next.gas);
        const note = new Notification(`${levelLabel(tts.lang, lvl)} · ${gas}`, {
          body: tts.phrase(lvl, next.gas, siteName(next.site)),
          tag: `expertgas-alarm-${next.id}`,
        });
        note.onclick = () => {
          window.focus();
          note.close();
        };
      } catch {
        /* notifications are best-effort */
      }
    }
    // Intentionally keyed on the active list; dedupe guarantees once-per-id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, overlayEnabled]);

  // Nothing active (everything cleared) → close.
  useEffect(() => {
    if (open && active.length === 0) setOpen(false);
  }, [open, active.length]);

  // The alert shown in the detail pane: the selection, else the newest active.
  const selected = useMemo<Alert | null>(
    () => active.find((a) => a.id === selectedId) ?? active[0] ?? null,
    [active, selectedId],
  );

  if (!open || active.length === 0 || !selected) return null;

  // Overlay grade colour = the worst active level right now.
  const worst = clampLevel(active.reduce((m, a) => Math.max(m, a.level ?? 0), 0));
  const color = worst >= 3 ? "var(--danger)" : "var(--warning)";

  // Single-alert 확인 / 해제. Both drop the row out of `active` (ack sets
  // state→acknowledged, clear sets cleared_at) so the surface shrinks one row
  // at a time and auto-closes on the last one. Feedback toasts carry a dedupe
  // key so ten rapid acks leave **one** line, not ten stacked cards.
  const busyId =
    (ack.isPending ? ack.variables?.id : undefined) ??
    (clear.isPending ? clear.variables : undefined) ??
    null;
  const onAck = (id: number) =>
    ack.mutate(
      { id },
      {
        onSuccess: () =>
          toast({ title: t("pages.alerts.toast.acked"), tone: "success", dedupe: "alert-ack" }),
        onError: () =>
          toast({ title: t("pages.alerts.toast.ackFailed"), tone: "danger", dedupe: "alert-ack" }),
      },
    );
  const onClear = (id: number) =>
    clear.mutate(id, {
      onSuccess: () =>
        toast({ title: t("pages.alerts.toast.cleared"), tone: "success", dedupe: "alert-clear" }),
      onError: () =>
        toast({ title: t("pages.alerts.toast.clearFailed"), tone: "danger", dedupe: "alert-clear" }),
    });

  // Bulk ack/clear over *every* active alert. One settled batch (bounded
  // concurrency, failures tallied) + a single feed invalidation — not the old
  // fire-and-forget per-alert `mutate()` loop that swallowed errors and
  // refetched N times. A completion toast reports any partial failure.
  const toastBulk = (okKey: string, failKey: string, res: BulkAlertResult) => {
    if (res.total === 0) return;
    if (res.failed === 0) {
      toast({ title: t(okKey, { n: res.ok }), tone: "success", dedupe: "alert-bulk" });
    } else {
      toast({
        title: t(failKey),
        description: t("pages.alerts.bulk.result", { ok: res.ok, failed: res.failed }),
        tone: "danger",
        dedupe: "alert-bulk",
      });
    }
  };
  const ackAll = async () => {
    const ids = active
      .filter((a) => a.id != null && a.state === "open")
      .map((a) => a.id!);
    if (ids.length === 0) return;
    toastBulk(
      "pages.alerts.bulk.ackedN",
      "pages.alerts.bulk.ackPartialFail",
      await ackBulk.mutateAsync(ids),
    );
  };
  const clearAll = async () => {
    const ids = active.filter((a) => a.id != null).map((a) => a.id!);
    if (ids.length === 0) return;
    toastBulk(
      "pages.alerts.bulk.clearedN",
      "pages.alerts.bulk.clearPartialFail",
      await clearBulk.mutateAsync(ids),
    );
  };
  // Voice stop = cancel the current utterance now + suppress re-speak until the
  // operator re-enables (shared tts store → consistent with the TopBar toggle
  // and Settings). The visual card stays up; the header ✕ dismisses it.
  const stopVoice = () => {
    if (tts.supported) {
      try {
        window.speechSynthesis.cancel();
      } catch {
        /* ignore — the enabled=false effect also cancels */
      }
    }
    tts.setEnabled(false);
  };
  const dismiss = () => setOpen(false);
  const goEvacuate = () => {
    setOpen(false);
    navigate("/evacuation");
  };

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-label={t("alarm.activeAria", {
        level: levelLabel(tts.lang, worst),
        n: active.length,
      })}
      className={cn(
        // NOT inset-0: the TopBar (음성 토글 · 언어 · 테마) and the Sidebar
        // (계정 메뉴) must stay reachable while an alarm is on screen.
        // ⚠️ fixed 는 셸의 상단 패딩을 받지 않는다. 상수 60px 로 두면 TopBar 가
        // 실제로 차지하는 [inset, inset+60] 구간을 z-90 으로 덮어, 위 주석이
        // 지키려던 **음성 토글·언어·테마가 경보 중에 눌리지 않는다**.
        // 사이드바는 lg 미만에서 숨으므로(hidden lg:flex) 좌측 비움도 lg 부터.
        // 하단은 탭바를 남겨 경보 중에도 경보·센서 탭으로 이동할 수 있게 한다.
        "fixed right-0 z-[90] top-[var(--chrome-top)]",
        "left-0 lg:left-[var(--sidebar-w)]",
        "bottom-[var(--tabbar-h)] lg:bottom-0",
        // Docked flush under the TopBar and stretched to fill the content area —
        // no centring, no floating-modal inset. On ≥lg keep the 119 승인 도크's
        // rail free so its 승인/반려 버튼 are never buried under this panel.
        "flex items-stretch lg:right-[var(--dispatch-dock-w)]",
      )}
      style={{
        background: "color-mix(in oklab, var(--bg) 78%, black 22%)",
        backdropFilter: "blur(6px)",
        animation: "fade-in var(--motion-fast) var(--ease-out-soft) both",
      }}
    >
      <div
        className="flex h-full w-full flex-col overflow-hidden border-t-[3px] text-[var(--fg)]"
        style={{
          borderColor: color,
          background: "var(--bg-elevated)",
          boxShadow: `0 8px 24px -12px color-mix(in oklab, ${color} 45%, transparent)`,
        }}
      >
        {/* ── grade band ─────────────────────────────────────────────── */}
        <div
          className="flex items-center gap-3 px-6 py-4"
          style={{ background: `color-mix(in oklab, ${color} 16%, transparent)` }}
        >
          <span
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full"
            style={{ background: color, color: "white" }}
          >
            <TriangleAlert size={24} strokeWidth={2.25} />
          </span>
          <div className="min-w-0">
            <div className="text-[12px] font-medium uppercase tracking-[0.08em] text-[var(--fg-subtle)]">
              {t("alarm.newAlert")}
            </div>
            <div
              className="text-[24px] font-bold leading-tight tracking-[-0.02em]"
              style={{ color }}
            >
              {t("alarm.activeCount", {
                level: levelLabel(tts.lang, worst),
                n: active.length,
              })}
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label={t("alarm.dismiss")}
            className="ml-auto grid h-9 w-9 place-items-center rounded-full text-[var(--fg-subtle)] transition-colors hover:bg-[var(--bg-hover)] hover:text-[var(--fg)]"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        {/* ── bulk toolbar ───────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-2 border-b border-[var(--separator)] px-6 py-3">
          {canAck && (
            <Button
              intent="primary"
              size="sm"
              onClick={ackAll}
              loading={ackBulk.isPending}
              disabled={ackBulk.isPending || !active.some((a) => a.state === "open")}
              leading={<CheckCircle2 size={15} strokeWidth={1.9} />}
            >
              {t("pages.alerts.ackAll")}
            </Button>
          )}
          {canClear && (
            <Button
              intent="outline"
              size="sm"
              onClick={clearAll}
              loading={clearBulk.isPending}
              disabled={clearBulk.isPending}
              leading={<XCircle size={15} strokeWidth={1.9} />}
            >
              {t("pages.alerts.clearAll")}
            </Button>
          )}
          <Button
            intent="ghost"
            size="sm"
            onClick={() => (tts.enabled ? stopVoice() : tts.setEnabled(true))}
            leading={
              tts.enabled ? (
                <VolumeX size={15} strokeWidth={1.9} />
              ) : (
                <Volume2 size={15} strokeWidth={1.9} />
              )
            }
          >
            {tts.enabled ? t("pages.alerts.voiceStop") : t("pages.alerts.voiceOn")}
          </Button>
          <Button
            intent="ghost"
            size="sm"
            onClick={goEvacuate}
            leading={<DoorOpen size={15} strokeWidth={1.9} />}
          >
            {t("alarm.evacuate")}
          </Button>

          {/* voice language quick-switch (also sets the app UI language) */}
          <div className="ml-auto flex flex-wrap items-center gap-1.5">
            <Languages
              size={14}
              className="mr-0.5 text-[var(--fg-subtle)]"
              strokeWidth={1.75}
            />
            {ALERT_LANGUAGES.map((lng) => {
              const isActive = tts.lang === lng;
              return (
                <button
                  key={lng}
                  type="button"
                  onClick={() => {
                    tts.setLangOverride(lng as Language);
                    changeLanguage(lng as Language);
                  }}
                  aria-pressed={isActive}
                  className={cn(
                    "rounded-full px-2.5 py-1 text-[12px] font-medium transition-colors",
                    isActive
                      ? "bg-[var(--accent)] text-white"
                      : "bg-[var(--bg-subtle)] text-[var(--fg-subtle)] hover:bg-[var(--bg-hover)]",
                  )}
                >
                  {LANG_LABEL[lng]}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── master-detail body ─────────────────────────────────────── */}
        {/* Two independently scrolling rows, not one long scroll: the detail
            stays pinned on top so a row click has a *visible* effect, and the
            list underneath keeps its own scrollbar so every pending alert is
            reachable without a "모두 보기" toggle. */}
        <div className="flex min-h-0 flex-1 flex-col gap-4 px-6 py-5">
          {/* detail (top) — the selected alert */}
          <div className="max-h-[45%] shrink-0 overflow-y-auto">
            <DetailPanel
              alert={selected}
              onAck={onAck}
              onClear={onClear}
              canAck={canAck}
              canClear={canClear}
            />
          </div>

          {/* pending list (bottom) — new alerts accumulate, 확인/해제 removes */}
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="mb-2 flex shrink-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[var(--fg-subtle)]">
                {t("alarm.activeList")}
              </span>
              <span className="text-[11.5px] text-[var(--fg-subtle)]">
                {t("alarm.listHint")}
              </span>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              <FeedCard
                isLoading={false}
                isError={false}
                list={active}
                selectedId={selected.id ?? null}
                onSelect={setSelectedId}
                onAck={onAck}
                onClear={onClear}
                canAck={canAck}
                canClear={canClear}
                busyId={busyId}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
